import { getMessage } from '@/shared/copy/messages';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { uiToneSeverityMap } from '@/theme/uiTokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { Alert } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';
import type { SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement, ReactNode } from 'react';

type SignInScreenFrameVariant = 'default' | 'darkSignIn' | 'darkSignUp';

type SignInScreenFrameProps = {
  title: string;
  sessionExpiredNotice: boolean;
  onDismissSessionExpired: () => void;
  children: ReactNode;
  shellMaxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  panelSx?: SxProps<Theme>;
  variant?: SignInScreenFrameVariant;
  subtitle?: string;
  contentGap?: SpacingToken;
};

const darkAuthBrandTextSx = { color: 'text.primary' } as const;
const darkAuthCaptionSx = { color: 'text.secondary' } as const;
const tallViewportMediaQuery = '@media (min-height: 1000px)';
const tallViewportPanelOffset = 'clamp(-80px, -5dvh, -48px)';

export const SignInScreenFrame = ({
  title,
  sessionExpiredNotice,
  onDismissSessionExpired,
  children,
  shellMaxWidth = 'xs',
  panelSx,
  variant = 'default',
  subtitle,
  contentGap,
}: SignInScreenFrameProps): ReactElement => {
  const isDarkAuth = variant === 'darkSignIn' || variant === 'darkSignUp';
  const displayTitle =
    variant === 'darkSignIn' && title === 'Sign In'
      ? 'Welcome Back'
      : variant === 'darkSignUp' && title === 'Sign Up'
        ? 'Create Your Account'
        : title;
  const displaySubtitle =
    subtitle ??
    (variant === 'darkSignIn'
      ? title === 'Reset Password'
        ? "Enter your email and we'll send you a verification code to reset your password."
        : 'Sign in to your account.'
      : title === 'Verify Email'
        ? 'Check your inbox to finish creating your account.'
        : 'Get started with FlowQA.');
  const panelContentGap =
    contentGap ??
    (isDarkAuth && (title === 'Verify Email' || title === 'Reset Password') ? 'md' : 'lg');

  return (
    <PageShell maxWidth={shellMaxWidth} paddingX="lg" paddingY="lg" paddingBottom="lg">
      <Box
        sx={{
          minHeight: { xs: 'calc(100dvh - 180px)', sm: 'calc(100dvh - 210px)' },
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          pt: { xs: 'calc(env(safe-area-inset-top) + 24px)', sm: '24px' },
          pb: { sm: '24px' },
        }}
      >
        <Stack
          gap="xl"
          alignItems="center"
          sx={{
            width: '100%',
            [tallViewportMediaQuery]: {
              transform: { sm: `translateY(${tallViewportPanelOffset})` },
            },
          }}
        >
          {sessionExpiredNotice ? (
            <Alert
              severity={uiToneSeverityMap.orange}
              color="orange"
              onClose={onDismissSessionExpired}
              sx={{ width: '100%' }}
            >
              {getMessage('auth.common.sessionExpiredSignIn')}
            </Alert>
          ) : null}
          <Box
            sx={[
              { width: '100%' },
              ...(Array.isArray(panelSx) ? panelSx : panelSx ? [panelSx] : []),
            ]}
          >
            <Panel
              padding="xl"
              gap="xl"
              contentGap={panelContentGap}
              sx={isDarkAuth ? raisedPanelSx : undefined}
            >
              {isDarkAuth ? (
                <Stack gap="lg" alignItems="flex-start">
                  <Stack direction="row" gap="sm" alignItems="center">
                    <Box
                      component="img"
                      src="/logo.png"
                      alt="FlowQA"
                      sx={{ height: '40px', width: '40px', display: 'block' }}
                    />
                    <Text role="sectionTitle" sx={darkAuthBrandTextSx}>
                      FlowQA
                    </Text>
                  </Stack>
                  <Stack gap="xs">
                    <Text role="sectionTitle" sx={darkAuthBrandTextSx}>
                      {displayTitle}
                    </Text>
                    <Text role="body" sx={darkAuthCaptionSx}>
                      {displaySubtitle}
                    </Text>
                  </Stack>
                </Stack>
              ) : (
                <Stack gap="sm" alignItems="center">
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="FlowQA"
                    sx={{ height: '44px', width: '44px', display: 'block' }}
                  />
                  <Text role="sectionTitle" sx={{ textAlign: 'center' }}>
                    {title}
                  </Text>
                </Stack>
              )}
              {children}
            </Panel>
          </Box>
        </Stack>
      </Box>
    </PageShell>
  );
};
